import { useRef, useState } from "react";
import { API } from "@/lib/api";
import { toast } from "sonner";

// Encapsulates the download-template + dry-run-preview + confirm-import + error-report
// flow shared by every master-list ImportExportButtons instance.
export function useFileImport({ entity, label = "records", facility, onImported, allowUpsert = true }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [upsert, setUpsert] = useState(false);
  const [preview, setPreview] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [committing, setCommitting] = useState(false);

  const downloadBlob = async (url, filename, opts = {}) => {
    const res = await fetch(url, { credentials: "include", ...opts });
    if (!res.ok) throw new Error();
    const blob = await res.blob();
    const href = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = href;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(href);
  };

  const downloadTemplate = async () => {
    try {
      await downloadBlob(`${API}/io/template/${entity}`, `${entity}-import-template.xlsx`);
    } catch {
      toast.error("Could not download the template");
    }
  };

  const doImport = async (file, dry) => {
    const fd = new FormData();
    fd.append("file", file);
    const params = [];
    if (facility) params.push(`facility=${encodeURIComponent(facility)}`);
    if (allowUpsert && upsert) params.push("mode=upsert");
    if (dry) params.push("dry_run=true");
    const qs = params.length ? `?${params.join("&")}` : "";
    const csrf = (document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/) || [])[1] || "";
    const res = await fetch(`${API}/io/import/${entity}${qs}`, { method: "POST", credentials: "include", headers: { "X-CSRF-Token": decodeURIComponent(csrf) }, body: fd });
    const data = await res.json();
    if (!res.ok) throw data;
    return data;
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const data = await doImport(file, true); // dry-run preview first
      setPendingFile(file);
      setPreview(data);
    } catch (err) {
      toast.error((err && err.detail) || "Import failed — check the file and try again");
    } finally {
      setBusy(false);
    }
  };

  const confirmImport = async () => {
    if (!pendingFile) return;
    setCommitting(true);
    try {
      const data = await doImport(pendingFile, false);
      setPreview(null);
      setPendingFile(null);
      setResult(data);
      if (data.created > 0 || data.updated > 0) {
        toast.success(`Imported ${data.created} · updated ${data.updated || 0} ${label}`);
        onImported?.();
      } else {
        toast.message("No new records were imported — see the report");
      }
    } catch (err) {
      toast.error((err && err.detail) || "Import failed");
    } finally {
      setCommitting(false);
    }
  };

  const closePreview = () => { setPreview(null); setPendingFile(null); };

  const downloadErrorReport = async () => {
    try {
      const csrf = (document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/) || [])[1] || "";
      await downloadBlob(`${API}/io/error-report/${entity}`, `${entity}-import-errors.xlsx`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": decodeURIComponent(csrf) },
        body: JSON.stringify({ rows: result?.failed_rows || [] }),
      });
    } catch {
      toast.error("Could not build the error report");
    }
  };

  return {
    inputRef, busy, result, setResult, upsert, setUpsert, preview, committing,
    downloadTemplate, onFile, confirmImport, closePreview, downloadErrorReport,
  };
}
